import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Upload, Shield } from "lucide-react";
import VerifiedBadge from "@/components/app/VerifiedBadge";
import { toast } from "sonner";

const FIELDS = [
  { key: "diplome_ordre", needs_file: true, placeholder: "N° d'inscription à l'Ordre (le diplôme est vérifié par l'Ordre)" },
  { key: "rpps", needs_file: false, placeholder: "11 chiffres" },
  { key: "identite", needs_file: true, placeholder: "Type (CNI / Passeport)" },
];

export default function Verifications() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [forms, setForms] = useState({});

  useEffect(() => {
    if (user) refresh();
    // eslint-disable-next-line
  }, []);

  const submit = async (field) => {
    const f = forms[field] || {};
    const fd = new FormData();
    if (f.value) fd.append("value", f.value);
    if (f.file) fd.append("file", f.file);
    try {
      await api.post(`/verifications/${field}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Vérification soumise");
      setForms((s) => ({ ...s, [field]: { value: "", file: null } }));
      refresh();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const v = user?.verifications || {};
  const totalDone = FIELDS.filter((f) => v[f.key]?.status === "verified").length;

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#FF7A5C] mb-2">Doctobook · Trust</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-deep flex items-center">
          {t("verification.title")}
          {user?.blue_check && <VerifiedBadge size={22} />}
        </h1>
        <p className="text-slate-600 mt-2">{t("verification.subtitle")}</p>
        <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold">{totalDone} / {FIELDS.length} validés</span>
            {user?.blue_check && <span className="text-[#618DB8] font-bold flex items-center"><Shield className="h-4 w-4 me-1" />Badge bleu actif</span>}
          </div>
          <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#618DB8] to-[#FF7A5C] transition-all" style={{ width: `${(totalDone / FIELDS.length) * 100}%` }} />
          </div>
        </div>
        {user?.blue_check && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5" /> {t("verification.all_done")}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {FIELDS.map((f) => {
          const status = v[f.key]?.status;
          const verified = status === "verified";
          return (
            <div key={f.key} className={`bg-white border-2 rounded-xl p-5 transition ${verified ? "border-emerald-200" : "border-slate-200"}`} data-testid={`verif-${f.key}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${verified ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {verified ? <CheckCircle2 className="h-5 w-5" /> : <Shield className="h-5 w-5" />}
                  </div>
                  <div>
                    <div className="font-display font-bold text-deep">{t(`verification.${f.key}`)}</div>
                    {verified && <div className="text-xs text-emerald-700 font-semibold">✓ {t("verification.status_verified")}</div>}
                  </div>
                </div>
                {verified && v[f.key]?.value && <div className="text-xs font-mono text-slate-500">{v[f.key].value}</div>}
              </div>
              {!verified && (
                <div className="mt-4 grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
                  <div>
                    <Label className="text-xs">{f.placeholder}</Label>
                    <Input
                      value={(forms[f.key]?.value) || ""}
                      onChange={(e) => setForms((s) => ({ ...s, [f.key]: { ...(s[f.key] || {}), value: e.target.value } }))}
                      className="mt-1 h-10"
                      data-testid={`verif-${f.key}-input`}
                    />
                  </div>
                  {f.needs_file && (
                    <div>
                      <Label className="text-xs flex items-center gap-1"><Upload className="h-3 w-3" />Document</Label>
                      <Input
                        type="file"
                        accept="application/pdf,image/*"
                        onChange={(e) => setForms((s) => ({ ...s, [f.key]: { ...(s[f.key] || {}), file: e.target.files?.[0] || null } }))}
                        className="mt-1 h-10"
                        data-testid={`verif-${f.key}-file`}
                      />
                    </div>
                  )}
                  <Button onClick={() => submit(f.key)} className="btn-primary h-10" data-testid={`verif-${f.key}-submit`}>
                    Soumettre
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
